// NewViewModal - ask for a view (guide 18.1). Start from the category
// search (what the drawings actually contain: sizes named in keynotes,
// categories, systems) or a preset, adjust the request, and BidLight
// generates the isometric and schedule. The view opens as a tab named
// after what was asked for.
//
// Guide 20.3: the short routes open this same dialog pre-filled (`prefill`:
// a system chip, a zone or floor plan, a filtered register, a selection).
// It asks what to include (isometric, plan, schedule), previews the row count
// and estimated hours before anything is saved, and saves as a draft or
// publishes straight away.
import React, { useEffect, useMemo, useState } from "react";
import { AutoComplete, Checkbox, Input, InputNumber, Modal, Radio, Select, message } from "antd";
import { viewsApi, levelName } from "./viewModel";
import { drawnSystems, sortedLevels } from "../glanceModel";
import { matchOption } from "components/SearchSelect/searchSelect";

const CONTEXT = [
  { value: "pathways", label: "Pathways (J-hooks, cable tray, ladder rack)" },
  { value: "rooms", label: "Rooms" },
  { value: "devices", label: "Connected devices" },
];
const LENGTHS = [
  { value: "model", label: "Revit model (exact)" },
  { value: "traced", label: "Traced graphic (measured)" },
  { value: "estimate", label: "Right-angle estimate" },
];

const INCLUDE = [
  { value: "iso", label: "Isometric" },
  { value: "plan", label: "Plan" },
  { value: "schedule", label: "Schedule" },
];

const titleFor = (s) => (s ? s.label.replace(/^./, (c) => c.toUpperCase()) : "");
const BLANK = { preset: "custom", systems: [], filter: {}, levels: [], zones: [], context: ["rooms"], lengthMethod: ["model", "traced", "estimate"], assumptions: {}, include: ["iso", "plan", "schedule"] };

export default function NewViewModal({ open, projectId, manifest, prefill = null, onClose, onCreated }) {
  const [presets, setPresets] = useState([]);
  const [q, setQ] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [title, setTitle] = useState((prefill && prefill.title) || "");
  const [request, setRequest] = useState(() => ({ ...BLANK, ...((prefill && prefill.request) || {}), filter: { ...(((prefill && prefill.request) || {}).filter || {}) } }));
  const [publish, setPublish] = useState(false);
  const [preview, setPreview] = useState(null);   // { rows, qty, lengthFt, hours, caveats } | { error }
  const [busy, setBusy] = useState(false);
  const [empty, setEmpty] = useState(null);

  // Before anything is saved: how many rows, how much, and the hours.
  useEffect(() => {
    if (!open) return undefined;
    setPreview(null);
    const t = setTimeout(() => {
      viewsApi(projectId, "/preview", { method: "POST", body: { request } })
        .then(setPreview).catch((e) => setPreview({ error: e.message }));
    }, 400);
    return () => clearTimeout(t);
  }, [open, projectId, request]);

  useEffect(() => { if (open) viewsApi(projectId, "/presets").then(setPresets).catch(() => setPresets([])); }, [open, projectId]);
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      viewsApi(projectId, `/search?q=${encodeURIComponent(q)}`).then((out) => setSuggestions(out.suggestions || [])).catch(() => setSuggestions([]));
    }, 200);
    return () => clearTimeout(t);
  }, [q, open, projectId]);

  const systems = useMemo(() => drawnSystems(manifest), [manifest]);
  // A room is matched by its name and number as the register writes them ("MDF 129").
  const roomOptions = useMemo(() => {
    const seen = new Set();
    return (manifest.levels || []).filter((l) => !(request.levels || []).length || request.levels.includes(l.id))
      .flatMap((l) => (l.rooms || []).map((r) => [r.name, r.number].filter(Boolean).join(" ")).filter(Boolean))
      .filter((label) => (seen.has(label) ? false : seen.add(label)))
      .sort((a, b) => a.localeCompare(b, undefined, { numeric: true }))
      .slice(0, 800).map((label) => ({ value: label, label }));
  }, [manifest, request.levels]);
  const levels = useMemo(() => [{ id: "below" }, ...sortedLevels(manifest), { id: "roof" }], [manifest]);
  const set = (patch) => setRequest((r) => ({ ...r, ...patch }));
  const setFilter = (k, v) => setRequest((r) => ({ ...r, filter: { ...r.filter, [k]: v === "" || v == null ? undefined : v } }));

  const pick = (s) => {
    setRequest((r) => ({ ...r, ...s.request, filter: { ...(s.request.filter || {}) }, preset: s.request.preset || "custom" }));
    setTitle(titleFor(s));
    // AutoComplete writes the option key into the box after onSelect.
    setTimeout(() => setQ(s.label), 0);
    setEmpty(null);
  };
  const pickPreset = (id) => {
    const p = presets.find((x) => x.id === id);
    if (!p) return;
    setRequest((r) => ({ ...r, ...p.request, filter: { ...(p.request.filter || {}) }, levels: p.request.levels || [], preset: id }));
    if (!title || presets.some((x) => x.label === title)) setTitle(id === "custom" ? "" : p.label);
  };

  const submit = async () => {
    if (!title.trim()) return message.warning("Name the view: it becomes the tab's name");
    setBusy(true);
    try {
      const view = await viewsApi(projectId, "", { method: "POST", body: { title: title.trim(), request, publish } });
      message.success(`${view.title}: ${view.summary.runs} runs, ${view.status === "published" ? "published" : "saved as a draft"}`);
      onCreated(view);
    } catch (e) {
      if (e.status === 422) setEmpty(e.body); else message.error(e.message);
    }
    setBusy(false);
    return undefined;
  };

  const f = request.filter || {};
  return (
    <Modal open={open} title="New view" okText={publish ? "Generate and publish" : "Generate view"} okButtonProps={{ disabled: !!(preview && !preview.error && preview.rows === 0) }}
           confirmLoading={busy} onOk={submit} onCancel={onClose} width={820} destroyOnClose>
      <div className="bl-glance g-modal">
        <label className="g-wide">
          What do you want documented?
          <AutoComplete value={q} onChange={setQ} style={{ width: "100%" }} placeholder='Search the drawings: 2", sprinkler, panels, roof...'
            options={suggestions.map((s) => ({
              value: s.key,
              label: (
                <div className="g-suggest">
                  <span>{s.label}</span>
                  <span className="g-tsub">{s.kind}, {s.count.toLocaleString()} in the drawings{s.detail ? `, ${s.detail}` : ""}</span>
                </div>
              ),
            }))}
            onSelect={(key) => { const s = suggestions.find((x) => x.key === key); if (s) pick(s); }} />
        </label>

        <div className="g-form">
          <label>View name (the tab's name)<Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="2-inch conduit register" /></label>
          <label>Preset
            <Select value={request.preset} onChange={pickPreset} options={presets.map((p) => ({ value: p.id, label: p.label }))} />
          </label>
        </div>

        <h3>Systems</h3>
        <Checkbox.Group value={request.systems} onChange={(v) => set({ systems: v })}
                        options={systems.map((s) => ({ value: s.id, label: s.label }))} />

        <h3>Element filter</h3>
        <div className="g-form">
          <label>Size<Input value={f.size || ""} onChange={(e) => setFilter("size", e.target.value)} placeholder='2"' /></label>
          <label>Keynote<Input value={f.keynote || ""} onChange={(e) => setFilter("keynote", e.target.value)} placeholder="KN 15" /></label>
          <label>Tag pattern<Input value={f.tagPattern || ""} onChange={(e) => setFilter("tagPattern", e.target.value)} placeholder="^V-6" /></label>
          <label>Category<Input value={f.category || ""} onChange={(e) => setFilter("category", e.target.value)} placeholder="Panels|Grounding bars" /></label>
          <label>Type<Input value={f.type || ""} onChange={(e) => setFilter("type", e.target.value)} placeholder="Dedicated" /></label>
          <label>Words in the note<Input value={f.text || ""} onChange={(e) => setFilter("text", e.target.value)} placeholder="riser|EZ-Path" /></label>
          <label>Height range (ft AFF)
            <span className="g-pair">
              <InputNumber min={0} value={(f.heightRange || [])[0]} onChange={(v) => setFilter("heightRange", v == null && (f.heightRange || [])[1] == null ? undefined : [v, (f.heightRange || [])[1]])} />
              <InputNumber min={0} value={(f.heightRange || [])[1]} onChange={(v) => setFilter("heightRange", v == null && (f.heightRange || [])[0] == null ? undefined : [(f.heightRange || [])[0], v])} />
            </span>
          </label>
        </div>

        <h3>Extent</h3>
        <div className="g-form">
          <label>Levels (none = all)
            <Select mode="multiple" value={request.levels} onChange={(v) => set({ levels: v })}
                    options={levels.map((l) => ({ value: l.id, label: levelName(manifest, l.id) }))} />
          </label>
          <label>Zones (none = all)
            <Select mode="multiple" value={request.zones} onChange={(v) => set({ zones: v })}
                    options={(manifest.zones || []).map((z) => ({ value: z.index, label: z.label }))} />
          </label>
          <label>Rooms (none = all)
            {/* Guide 18.1 extent: rooms by name and number, on the levels picked. */}
            <Select filterOption={matchOption} mode="multiple" showSearch value={request.rooms || []} onChange={(v) => set({ rooms: v })} optionFilterProp="label"
                    placeholder="e.g. MDF 129"
                    options={roomOptions} />
          </label>
        </div>

        <h3>Context layers and length</h3>
        <Checkbox.Group value={request.context} onChange={(v) => set({ context: v })} options={CONTEXT} />
        <div className="g-form" style={{ marginTop: 8 }}>
          <label>Length method
            <Select mode="multiple" value={request.lengthMethod} onChange={(v) => set({ lengthMethod: v })} options={LENGTHS} />
          </label>
          <label>Floor-to-floor height (ft), for riser estimates
            <InputNumber min={0} value={request.assumptions && request.assumptions.floorToFloorFt} onChange={(v) => set({ assumptions: { ...(request.assumptions || {}), floorToFloorFt: v } })} />
          </label>
          <label>Ceiling height (ft), where the documents give none
            <InputNumber min={0} max={40} step={0.5} value={request.assumptions && request.assumptions.ceilingFt} onChange={(v) => set({ assumptions: { ...(request.assumptions || {}), ceilingFt: v } })} />
          </label>
          <label>Sleeve allowance (ft a sleeve), where a sleeve is not drawn to length
            <InputNumber min={0} max={20} step={0.5} value={request.assumptions && request.assumptions.sleeveAllowanceFt} onChange={(v) => set({ assumptions: { ...(request.assumptions || {}), sleeveAllowanceFt: v } })} />
          </label>
        </div>
        <h3>What the view shows, and how it is saved</h3>
        <div className="g-form">
          <label>Include
            <Checkbox.Group value={request.include} onChange={(v) => set({ include: v.length ? v : ["schedule"] })} options={INCLUDE} />
          </label>
          <label>Save as
            <Radio.Group value={publish} onChange={(e) => setPublish(e.target.value)}
                         options={[{ value: false, label: "Draft, for review" }, { value: true, label: "Published, for the field to track" }]} />
          </label>
        </div>
        {(request.elementKeys || []).length > 0 && (
          <p className="g-tsub">Made from a selection of {request.elementKeys.length} element{request.elementKeys.length === 1 ? "" : "s"}: only those are kept.</p>
        )}
        <div className="g-callout" role="status" aria-live="polite">
          {!preview && "Counting what this request finds…"}
          {preview && preview.error && `The preview could not be made: ${preview.error}`}
          {preview && !preview.error && (
            <>
              <b>{preview.rows.toLocaleString()} row{preview.rows === 1 ? "" : "s"}</b>, quantity {preview.qty.toLocaleString()}
              {preview.measured ? `, ${preview.lengthFt.toLocaleString()} ft measured on ${preview.measured} of them` : ""}
              {preview.hours ? `; about ${Math.round(preview.hours.modeling).toLocaleString()} hours to model and document${preview.hours.notPriced ? ` (${preview.hours.notPriced} item${preview.hours.notPriced === 1 ? "" : "s"} have no production rate yet)` : ""}` : ""}.
              {(preview.caveats || []).length > 0 && <div className="g-tsub">{preview.caveats.slice(0, 4).join(". ")}.</div>}
            </>
          )}
        </div>
        <p className="g-tsub">The view keeps the request it was made from, so it can be generated again when the building data changes. Nothing it cannot measure is guessed: every length says how it was obtained.</p>
        {empty && (
          <div className="g-callout">
            <b>Nothing in the received documents matches this request.</b>
            {(empty.caveats || []).length ? ` ${empty.caveats.join(". ")}.` : ""} Try the search above to see what the drawings contain.
          </div>
        )}
      </div>
    </Modal>
  );
}
